/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */

/**
 * Saga to check features flags
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Evgeniy Naumov
 * @version 1.23.0
 * @since 1.0.0
 */

import filter from 'lodash/filter';
import get from 'lodash/get';
import { select } from 'redux-saga/effects';

import { makeSelectAuthFeatures } from '../../../pages/PageAuth/selectors/authSelectors';
import { IUserFeature } from '../../../pages/PageAuth/types/authTypes';
import { Access } from '../constants/guard/accessGuardConstants';
import { AccessComponentKeyEnum, FeatureNameEnum } from '../constants/guard/guardConstants';
import { IAccessItemEnabledParam } from '../types/guardTypes';

export function* checkGuardFeatureSaga(componentKey: AccessComponentKeyEnum) {
    // user features
    const features: IUserFeature[] = yield select(makeSelectAuthFeatures());
    // feature name for current component
    const feature: IAccessItemEnabledParam<FeatureNameEnum> | FeatureNameEnum | undefined = get(
        Access[componentKey],
        'featureName',
    );
    const version: number | number[] = Access[componentKey]?.featureVersion ?? 1;
    const featureVersion: number[] = Array.isArray(version) ? version : [version];

    if (feature) {
        const { name, isEnabled } = feature as IAccessItemEnabledParam<FeatureNameEnum>;
        const featureAccessArray = name
            ? filter(features, (f) => f.name === name && f.enabled === isEnabled && featureVersion.includes(f.version))
            : filter(features, (f) => f.name === feature && f.enabled && featureVersion.includes(f.version));
        return featureAccessArray.length > 0;
    }
    const featureNamesOr: FeatureNameEnum[] | undefined = get(Access[componentKey], 'featureNamesOr');
    if (featureNamesOr?.length) {
        return (
            featureNamesOr.filter((searchFeature) =>
                features
                    .filter((item) => item.enabled)
                    .map((item) => item.name)
                    .includes(searchFeature),
            ).length > 0
        );
    }
    const featureNamesAnd: FeatureNameEnum[] | undefined = get(Access[componentKey], 'featureNamesAnd');
    if (featureNamesAnd?.length) {
        return (
            featureNamesAnd.filter((searchFeature) =>
                features
                    .filter((item) => item.enabled)
                    .map((item) => item.name)
                    .includes(searchFeature),
            ).length === featureNamesAnd.length
        );
    }
    return true;
}
